import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Store, Check, Plug, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * Compact card shown on the dashboard. Shows whether the configured Shopify
 * shop is connected; clicking "Connect" kicks off OAuth (full-page redirect
 * to Shopify, then back to /app?shopify_connected=1).
 */
export default function ShopifyConnectCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [shopInput, setShopInput] = useState("");
  const [params, setParams] = useSearchParams();

  const refresh = async () => {
    try {
      const s = await api.shopifyStatus();
      setStatus(s);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  // Catch ?shopify_connected=1 redirect-back from OAuth callback
  useEffect(() => {
    if (params.get("shopify_connected") === "1") {
      toast.success(`Connected to ${params.get("shop") || "Shopify"}`);
      params.delete("shopify_connected");
      params.delete("shop");
      setParams(params, { replace: true });
      refresh();
    }
    // eslint-disable-next-line
  }, []);

  if (loading || !status) {
    return (
      <div className="lp-card p-5 flex items-center gap-3 text-sm text-gray-500" data-testid="shopify-card-loading">
        <Loader2 size={16} className="animate-spin" /> Checking Shopify status…
      </div>
    );
  }

  const connect = () => {
    // Prefer the user-typed shop domain, else fall back to server-configured default.
    let target = (shopInput || status.shop || "").trim().toLowerCase();
    if (target && !target.endsWith(".myshopify.com")) {
      target = target.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
      if (!target.endsWith(".myshopify.com")) target = target + ".myshopify.com";
    }
    if (!target || !/^[a-z0-9-]+\.myshopify\.com$/.test(target)) {
      toast.error("Enter a valid shop domain like your-store.myshopify.com");
      return;
    }
    setBusy(true);
    // Full-page redirect to backend, which redirects to Shopify
    const url = `${window.location.origin}/api/shopify/auth/install?shop=${encodeURIComponent(target)}`;
    window.location.href = url;
  };

  const disconnect = async () => {
    if (!window.confirm(`Disconnect Launchpad from ${status.shop}? Customers can't pre-order until you reconnect.`)) return;
    setBusy(true);
    try {
      await api.shopifyDisconnect();
      toast.success("Disconnected — back to mock mode");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (status.connected) {
    return (
      <div className="lp-card p-5 flex items-center gap-4 flex-wrap" data-testid="shopify-card-connected">
        <div className="w-10 h-10 rounded-xl bg-emerald-50 border border-emerald-200 flex items-center justify-center shrink-0">
          <Check size={18} className="text-emerald-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">Connected to Shopify</span>
            <span className="lp-badge lp-badge-preorder text-[10px]">Live</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate" data-testid="shopify-connected-shop">
            {status.shop} · {(status.scopes || []).length} scope{(status.scopes || []).length === 1 ? "" : "s"} granted
          </div>
        </div>
        <button
          onClick={disconnect}
          disabled={busy}
          className="lp-btn-secondary text-xs"
          data-testid="shopify-disconnect-btn"
        >
          Disconnect
        </button>
      </div>
    );
  }

  return (
    <div className="lp-card p-5 space-y-4" data-testid="shopify-card-disconnected">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-xl bg-[#FCE9E7] border border-[#F5C4C0] flex items-center justify-center shrink-0">
          <AlertCircle size={18} className="text-[#d0473e]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[#111] flex items-center gap-2">
            Shopify not connected <Store size={14} className="text-gray-400" />
          </div>
          <div className="text-xs text-[#6D7175] mt-0.5">
            Enter your Shopify store domain below to connect. Real products + real orders start once connected.
          </div>
        </div>
      </div>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <label className="lp-label" htmlFor="shopify-shop-input">Store domain</label>
          <input
            id="shopify-shop-input"
            className="lp-input font-mono text-sm"
            placeholder={status.shop || "your-store.myshopify.com"}
            value={shopInput}
            onChange={(e) => setShopInput(e.target.value)}
            data-testid="shopify-shop-input"
          />
        </div>
        <button
          onClick={connect}
          disabled={busy}
          className="lp-btn-primary text-xs"
          data-testid="shopify-connect-btn"
        >
          <Plug size={14} />
          {busy ? "Redirecting…" : "Connect Shopify"}
        </button>
      </div>
    </div>
  );
}
